import "./ReceptionPhoto.css";
import { FaUserCircle } from "react-icons/fa";
import { useEffect, useState } from "react";
import { formatDate, formatRelativeDate, isMoreThanOneDay } from "../../utils/date";
import { capitalize } from "../../utils/string";
import { Clock } from "lucide-react";
import { TimeAgo } from "../TimeAgo/TimeAgo";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

function UserLinkComp({ children, className, ...props }) {
	if (props.userUuid && props.showUserLink) {
		return (
			<Link to={`/device-users/${props.userUuid}`} className={className}>
				{children}
			</Link>
		);
	}
	return (
		<>
			{children}
		</>
	);
}

const ReceptionPhoto = ({ animateZoom = true, timeToZoom = 850, showUserLink = false, userUuid, ...props }) => {
	const { t } = useTranslation();
	const [relativeTime, setRelativeTime] = useState(null);
	const [urlPhoto, setUrlPhoto] = useState();
	const [mouseInside, setMouseInside] = useState(false);
	const [zoomPhoto, setZoomPhoto] = useState(false);
	const [showDefaultImage, setShowDefaultImage] = useState(false);

	useEffect(() => {
		setUrlPhoto(props.urlLogo);
	}, [props.urlLogo]);

	useEffect(() => {
		function updateRelativeTime() {
			setRelativeTime(`${formatRelativeDate(props.date)}`.replace('atrás', ''));
		}
		updateRelativeTime();
		const intervalId = setInterval(updateRelativeTime, 1000 * 30);
		return () => clearInterval(intervalId);
	}, [props]);

	useEffect(() => {
		if (!animateZoom) return;
		let time = timeToZoom;
		let timeout = null;
		let raf = null;
		let start = null;

		const radius = 8;
		const circumference = 2 * Math.PI * radius;

		let progressEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
		progressEl.setAttribute('width', '40');
		progressEl.setAttribute('height', '40');
		progressEl.setAttribute('viewBox', '0 0 50 50');

		progressEl.style.position = 'fixed';
		progressEl.style.pointerEvents = 'none';
		progressEl.style.transform = 'translate(-50%, -50%)';
		progressEl.style.zIndex = '9999';

		const circleBack = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
		circleBack.setAttribute('cx', '25');
		circleBack.setAttribute('cy', '25');
		circleBack.setAttribute('r', radius);
		circleBack.setAttribute('fill', 'none');
		circleBack.setAttribute('stroke', 'var(--bodyBackgroundColor)');
		circleBack.setAttribute('stroke-width', '10');
		circleBack.setAttribute('stroke-linecap', 'round');

		progressEl.appendChild(circleBack);

		const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
		circle.setAttribute('cx', '25');
		circle.setAttribute('cy', '25');
		circle.setAttribute('r', radius);
		circle.setAttribute('fill', 'none');
		circle.setAttribute('stroke', 'var(--primaryColor)');
		circle.setAttribute('stroke-width', '6');
		circle.setAttribute('stroke-linecap', 'round');

		circle.setAttribute('stroke-dasharray', circumference);
		circle.setAttribute('stroke-dashoffset', circumference);

		progressEl.appendChild(circle);

		let targetX = 0;
		let targetY = 0;

		const onMouseMove = (e) => {
			targetX = e.clientX;
			targetY = e.clientY;
			progressEl.style.left = `${targetX}px`;
			progressEl.style.top = `${targetY}px`;
		};

		function animateProgress(timestamp) {
			if (!start) start = timestamp;

			const elapsed = timestamp - start;
			const duration = time;
			const progress = Math.min(elapsed / duration, 1);

			const offset = circumference * (1 - progress);
			circle.setAttribute('stroke-dashoffset', offset);

			if (progress < 1) {
				raf = requestAnimationFrame(animateProgress);
			}
		}

		function clearProgress() {
			cancelAnimationFrame(raf);
			document.removeEventListener('mousemove', onMouseMove);

			if (progressEl.parentNode) {
				progressEl.parentNode.removeChild(progressEl);
			}
		}

		function clear() {
			clearTimeout(timeout);
			setZoomPhoto(false);
			clearProgress();
		}

		if (mouseInside) {
			document.body.appendChild(progressEl);
			document.addEventListener('mousemove', onMouseMove);
			raf = requestAnimationFrame(animateProgress);

			timeout = setTimeout(() => {
				setZoomPhoto(true);
				clearProgress();
			}, time);
		} else {
			clear();
		}

		return () => clear();
	}, [mouseInside]);

	return props.smallPhoto ? (
		<div className="small-photo" data-direction={props.direction} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
			<div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
				{(urlPhoto && !showDefaultImage) ? (
					<div
						className={`reception-image ${zoomPhoto ? "zoom" : ""}`}
						onMouseEnter={(e) => { setMouseInside(true) }}
						onMouseLeave={(e) => { setMouseInside(false) }}
					>
						<img src={urlPhoto} alt="profile" onError={(e) => { setShowDefaultImage(true) }} />
						<span className="">{t('common.denied')}</span>
					</div>
				) : (
					<div className="reception-image">
						<FaUserCircle size={70} color={"#a7a7a7"} />
					</div>
				)}
				<div style={{ marginLeft: "17px", flex: 1 }}>
					<p className="reception-person-name text-[0.9rem]!">
						<UserLinkComp userUuid={userUuid} showUserLink={showUserLink}>
							{props.name?.indexOf(" ") !== -1 ? (
								<>
									<b>
										{capitalize(props.name).substring(0, props.name.indexOf(" "))}
									</b>
									<span className="font-normal!" style={{ marginLeft: "0px", opacity: 0.85 }}>
										{capitalize(props.name).slice(props.name.indexOf(" "))}
									</span>
								</>
							) : (
								<b>
									{capitalize(props.name)}
								</b>
							)}
						</UserLinkComp>
					</p>
					<p className="flex justify-start items-center mt-1.5!" style={{ color: "var(--textColor)" }}>
						<span className="inline-flex text-[13px]! leading-3.75! opacity-60!">
							<Clock size={12} className="inline-flex mr-[0.24rem]! pt-px! opacity-75!" />
							<span>
								{!isMoreThanOneDay(props.date) ? (
									<span>
										<TimeAgo date={props.date} simple={true} showText={true} />
									</span>
								) : (
									`${formatDate(props.date, true)}`
								)}
							</span>
						</span>
						<span className="inline-flex mx-1! text-[8px]! text-(--textColor)! opacity-60!">
							•
						</span>
						<b className="inline-flex text-[13px]! font-semibold! leading-3.75! opacity-60!">
							{props.subtitle}
						</b>
					</p>
					{/* {(props.origin && props.destiny && (props.origin !== 'Desconhecido' || props.destiny !== 'Desconhecido')) && (
						<span className={`text-[13px]! leading-3.75! mt-[0.3rem]! ${props.direction === 'enter' ? 'entry' : 'exit'}`} style={{ color: "var(--textColor)", opacity: 0.6, display: "inline-flex", alignItems: "center" }}>
								{props.origin ?? "Não consta"}
								<TiArrowRightThick style={{ margin: "0 5px" }} />
								{props.destiny ?? "Não consta"}
							</span>
					)} */}
				</div>
			</div>
		</div>
	) : (
		<div
			style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
		>
			{!props.hidePhoto && (
				<div
					className={`reception-image ${zoomPhoto ? "zoom" : ""}`}
					onMouseEnter={(e) => { setMouseInside(true) }}
					onMouseLeave={(e) => { setMouseInside(false) }}
				>
					{(urlPhoto && !showDefaultImage) ? (
						<img src={urlPhoto} alt="profile" onError={(e) => { setShowDefaultImage(true) }} />
					) : (
						<FaUserCircle size={100} color={"#a7a7a7"} />
					)}
				</div>
			)}
			{props.name && (
				<div style={{ textAlign: "center", marginTop: "10px" }}>
					<p className="reception-person-name">
						<UserLinkComp userUuid={userUuid} showUserLink={showUserLink} className="hover:opacity-65 duration-200">
							{props.name}
						</UserLinkComp>
					</p>
				</div>
			)}
		</div>
	);
};

export default ReceptionPhoto;
